import { addDays, format, parse, parseISO } from 'date-fns';

/**
 * Canonical date formats. The `DATE`–`SHORT_DATE` entries are display formats
 * (EHR best practice — `Mon D, YYYY`). The `ISO_*` entries are storage/data
 * formats (FHIR resources, internal date keys, form state, HTML input values) —
 * never use them for user-facing display.
 *
 * Always reference these constants rather than inlining a format string, so the
 * choice of "display vs storage" is made once, at the constant, not at each call.
 */
export const DATE_FORMATS = {
  /** Standard display date — all clinical/admin views. e.g. "Jun 3, 2025" */
  DATE: 'MMM d, yyyy',
  /** Timestamp with time. e.g. "Jun 3, 2025 2:30 PM" */
  DATE_TIME: 'MMM d, yyyy h:mm a',
  /**
   * Timestamp led by the weekday — for a date the reader has to hold in their head and check against
   * the rest of their week, rather than one they are scanning in a list. e.g. "Thursday, Aug 27, 2026 2:30 PM"
   */
  WEEKDAY_DATE_TIME: 'EEEE, MMM d, yyyy h:mm a',
  /**
   * {@link WEEKDAY_DATE_TIME} for locales that write the day before the month (es). Pair with the
   * matching `date-fns` locale: the locale translates the weekday and month names, the pattern decides
   * the order, and neither does the other's job. e.g. "jueves, 27 ago 2026 2:30 PM"
   */
  WEEKDAY_DATE_TIME_DAY_FIRST: 'EEEE, d MMM yyyy h:mm a',
  /** Timestamp with an "at" separator — reads more naturally in prose (e.g. tooltips). e.g. "Jun 3, 2025 at 2:30 PM" */
  DATE_TIME_VERBOSE: "MMM d, yyyy 'at' h:mm a",
  /** Timestamp with the viewer's timezone offset — use when the reader may be in a different zone than the event. e.g. "Jun 3, 2025 2:30 PM (GMT-04:00)" */
  DATE_TIME_ZONE: 'MMM d, yyyy h:mm a (OOOO)',
  /** Time only. e.g. "2:30 PM" */
  TIME: 'h:mm a',
  /** Date ranges, timing summaries. e.g. "Jun 2025" */
  MONTH_YEAR: 'MMM yyyy',
  /** Within-year contexts (scheduling, calendars). e.g. "Jun 3" */
  SHORT_DATE: 'MMM d',
  /** Weekday + short date. e.g. "Mon, Jun 3" */
  WEEKDAY_DATE: 'EEE, MMM d',
  /** Timezone offset alone — for stating once which zone a list of times is in. e.g. "GMT-04:00" */
  ZONE: 'OOOO',
  /** ISO date — storage/data only (FHIR dates, date keys, form state). e.g. "2025-06-03" */
  ISO_DATE: 'yyyy-MM-dd',
  /** HTML `datetime-local` input value — storage/data only. e.g. "2025-06-03T14:30" */
  ISO_DATETIME_LOCAL: "yyyy-MM-dd'T'HH:mm",
} as const;

/** Today's date (or `daysOffset` days from today) as a YYYY-MM-DD string. */
export function getDateString(daysOffset: number = 0): string {
  return format(addDays(new Date(), daysOffset), DATE_FORMATS.ISO_DATE);
}

/** Parse a YYYY-MM-DD string into a Date at local midnight. */
export function parseDateString(dateStr: string): Date {
  return parse(dateStr, DATE_FORMATS.ISO_DATE, new Date());
}

/**
 * Coerce a `string | Date` timestamp to a `Date`. Strings are parsed with `parseISO` (never
 * `new Date(str)`, per the `date-rules/no-new-date-date-string` rule); `Date` values pass through.
 * For the `string | Date` shape common on API-returned resources (e.g. `createdAt`).
 */
export function toDate(value: string | Date): Date {
  return typeof value === 'string' ? parseISO(value) : value;
}

/**
 * Formats a date string to a readable date and time format
 * @param dateString - ISO date string or null/undefined
 * @returns Formatted date string like "Jan 15, 2024 2:30 PM" or "Unknown"
 */
export const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Unknown';
  try {
    const date = parseISO(dateString);
    return format(date, DATE_FORMATS.DATE_TIME);
  } catch {
    return dateString;
  }
};

/**
 * Formats a date string to a readable date format (without time)
 * @param dateString - ISO date string or null/undefined
 * @returns Formatted date string like "Jan 15, 2024" or "Unknown"
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Unknown';
  try {
    const date = parseISO(dateString);
    return format(date, DATE_FORMATS.DATE);
  } catch {
    return dateString;
  }
};

/**
 * Like {@link formatDate} but omits the year when it matches the current year,
 * e.g. "Jun 9" this year vs "Jun 9, 2024" in other years. Useful in dense lists
 * where the year is noise for recent entries.
 * @param now - reference "today" for the current-year comparison (injectable for tests)
 */
export const formatDateOmitCurrentYear = (dateString: string | null | undefined, now = new Date()): string => {
  if (!dateString) return 'Unknown';
  try {
    const date = parseISO(dateString);
    const fmt = date.getFullYear() === now.getFullYear() ? DATE_FORMATS.SHORT_DATE : DATE_FORMATS.DATE;
    return format(date, fmt);
  } catch {
    return dateString;
  }
};
